/**
 * Copyright © IMPACT Commerce AB. All rights reserved.
 * See COPYING.txt for license details.
 */

import { Component } from '@theme/component';

/** Touch devices without hover — thumbnails are always visible; see product-variations.liquid */
const mediaQueryTouch = matchMedia('(hover: none) and (pointer: coarse)');

/**
 * Wires up hover-preview + tap-to-select interaction for a set of variation
 * thumbnails (each marked `data-variation-hover-init`) inside a product card:
 * hovering/focusing swaps the card's main image and price; on desktop a
 * click navigates natively, on touch the first tap selects and the second
 * follows the link. Shared by `card_hover`'s `<card-variation-hover>` and by
 * `buttons` mode's inline swatches (assets/product-variations.js) when
 * rendered inside a product-card.
 *
 * @param {HTMLElement} container - Element whose descendants carry `data-variation-hover-*`.
 * @param {HTMLElement} productCard - The ancestor `<product-card>` / `.resource-card`.
 * @returns {() => void} Cleanup function that removes every listener this created.
 */
export function bindCardVariationHover(container, productCard) {
  /** @type {HTMLImageElement | null} */
  const mainImage = productCard.querySelector(
    'slideshow-slide img, .card-gallery img, .media img, img.resource-card__image, img[src*="products"]',
  );
  const priceElement = productCard.querySelector('product-price [ref="priceContainer"] .price');
  const productLink = /** @type {HTMLAnchorElement | null} */ (productCard.querySelector('[ref="productCardLink"]'));
  const galleryLink = /** @type {HTMLAnchorElement | null} */ (productCard.querySelector('[ref="cardGalleryLink"]'));
  const titleLink = /** @type {HTMLAnchorElement | null} */ (productCard.querySelector('[ref="productTitleLink"]'));
  const primaryVariation = /** @type {HTMLElement | null} */ (
    container.querySelector('[data-variation-hover-primary]')
  );

  /** @type {HTMLElement | null} Currently selected/previewed thumbnail; falls back to primaryVariation. */
  let selectedVariation = primaryVariation;

  const mainImageSizes = mainImage?.sizes?.replace(/^\s*auto\s*,\s*/i, '');

  // Slide 2 shows the secondary/hover media; swap it when a variation is picked.
  // resource-card (search/mega-menu) uses `.resource-card__hover-slideshow`;
  // the standard product-card on the PLP uses `.card-gallery`. Support both so
  // the card's second image tracks the selected colourway everywhere.
  const hoverSlideshow =
    productCard.querySelector('.resource-card__hover-slideshow') ?? productCard.querySelector('.card-gallery');
  const secondSlide = /** @type {HTMLElement | null} */ (
    hoverSlideshow?.querySelectorAll('slideshow-slide')[1] ?? null
  );
  const hoverDots = /** @type {HTMLElement | null} */ (hoverSlideshow?.querySelector('slideshow-controls') ?? null);

  /** @type {HTMLImageElement | null} */
  // Ignore any <img> that lives INSIDE the <video> — that's the video's
  // poster/fallback child, which the browser never renders while the video is
  // playable, so swapping its src would be invisible (0×0). We want a real,
  // separately-rendered image sibling; if there isn't one, the block below
  // creates it.
  let secondSlideImg = /** @type {HTMLImageElement | null} */ (
    Array.from(secondSlide?.querySelectorAll('img') ?? []).find((img) => !img.closest('video')) ?? null
  );
  const secondSlideVideo = /** @type {HTMLVideoElement | null} */ (secondSlide?.querySelector('video') ?? null);

  // Remember original slide-2 state so we can restore on primary
  const originalSecondary = secondSlideImg
    ? { src: secondSlideImg.src, srcset: secondSlideImg.srcset, alt: secondSlideImg.alt }
    : null;

  // The primary colourway's own hover video is rendered with <source> children
  // (so `src` is usually empty). Non-primary colourways can repoint this same
  // <video> at their own clip; remember the original so we can restore it.
  const originalVideoSrc = secondSlideVideo?.getAttribute('src') ?? null;
  let videoSwapped = false;

  /**
   * Reliable show/hide for slide-2 media. `.product-media` is a flex row and
   * the stylesheet forces `display:block` on `.product-media__image` and the
   * hover video — which defeats the `hidden` attribute, so a "hidden" element
   * would still take a flex slot and split the card in half. Toggle inline
   * `display` (which wins over the stylesheet) alongside `hidden`.
   * @param {HTMLElement | null} el
   */
  function showMedia(el) {
    if (!el) return;
    el.hidden = false;
    el.style.display = '';
  }

  /** @param {HTMLElement | null} el */
  function hideMedia(el) {
    if (!el) return;
    el.hidden = true;
    el.style.display = 'none';
  }

  /**
   * Play a hover <video> once it has data (muted playback needs no gesture).
   * @param {HTMLVideoElement} video
   */
  function playVideoEl(video) {
    const play = () => video.play?.()?.catch(() => {});
    if (video.readyState > 0) play();
    else video.addEventListener('loadeddata', play, { once: true });
  }

  /**
   * Point the hover <video> at a colourway's own clip and play it. A `src`
   * attribute wins over <source> children in the resource-selection algorithm,
   * so this cleanly replaces the primary's clip.
   * @param {HTMLVideoElement} video
   * @param {string} url
   */
  function swapVideoSource(video, url) {
    if (video.getAttribute('src') === url) {
      playVideoEl(video);
      return;
    }
    video.pause();
    video.setAttribute('src', url);
    video.load();
    playVideoEl(video);
  }

  /**
   * Restore the hover <video> to the primary colourway's original source.
   * @param {HTMLVideoElement} video
   */
  function restoreVideoSource(video) {
    video.pause();
    if (originalVideoSrc) {
      video.setAttribute('src', originalVideoSrc);
    } else {
      // Original played from <source> children — drop the override to re-enable them.
      video.removeAttribute('src');
    }
    video.load();
  }

  // If slide 2 only has a video, create a hidden <img> we can swap in when a
  // non-primary colourway is selected. Mirror the video's own classes (minus
  // the *-hover-video modifier) so the image inherits the same sizing/object-fit
  // — `product-media__image` on the PLP, `resource-card__image` in search — and
  // insert it inside the video's own wrapper (.product-media) so it fills the
  // slide instead of collapsing to nothing.
  if (secondSlide && secondSlideVideo && !secondSlideImg) {
    secondSlideImg = document.createElement('img');
    secondSlideImg.className =
      secondSlideVideo.className
        .replace(/(?:product-media|resource-card)__hover-video/g, '')
        .replace(/\s+/g, ' ')
        .trim() || 'product-media__image';
    secondSlideImg.loading = 'lazy';
    secondSlideImg.alt = '';
    (secondSlideVideo.parentElement ?? secondSlide).insertBefore(secondSlideImg, secondSlideVideo.nextSibling);
    // Hide via inline display (not just `hidden`) so it doesn't hold a flex
    // slot next to the video and split the card before any colourway is picked.
    hideMedia(secondSlideImg);
  }

  container.setAttribute('role', 'group');
  container.setAttribute('aria-label', 'Product color variations');

  /** @param {HTMLElement} element */
  function applyVariation(element) {
    const data = element.dataset;
    const variationHoverImage = data.variationHoverImage || data.variationImage;
    const variationHoverSrcset = data.variationHoverSrcset || data.variationImageSrcset;
    const variationHoverPrice = data.variationHoverPrice;
    const variationHoverUrl = data.variationHoverUrl || data.variationUrl;

    if (mainImage && variationHoverImage) {
      mainImage.src = variationHoverImage;
      mainImage.srcset = variationHoverSrcset || '';
    }

    if (priceElement && variationHoverPrice) {
      priceElement.textContent = variationHoverPrice;
    }

    // The pre-sale label follows the colourway (assets/pre-sale-label.js).
    productCard.dispatchEvent(
      new CustomEvent('pre-sale:refresh', { bubbles: true, detail: { percentage: data.variationPreSale ?? null } }),
    );

    if (variationHoverUrl) {
      if (productLink) productLink.href = variationHoverUrl;
      if (galleryLink) galleryLink.href = variationHoverUrl;
      if (titleLink) titleLink.href = variationHoverUrl;
    }

    // Update slide 2 with the selected colourway's secondary media (its own
    // hover video if it has one, otherwise its secondary image).
    if (hoverSlideshow) {
      const isPrimary = !primaryVariation || element === primaryVariation;
      const secondaryImage = data.variationHoverSecondaryImage;
      const secondarySrcset = data.variationHoverSecondarySrcset;
      const secondaryVideo = data.variationHoverSecondaryVideo;

      // Exactly ONE of {video, image} is ever shown in the slide — the other
      // is fully removed from the flex row via hideMedia (inline display:none),
      // otherwise both hold a slot and the card splits in half.
      if (isPrimary) {
        // Primary colourway: restore + show its own media (video if it has one).
        if (secondSlideVideo && videoSwapped) {
          restoreVideoSource(secondSlideVideo);
          videoSwapped = false;
          // Switching back mid-hover: resume the primary clip so it doesn't
          // sit on a frozen frame. Skip when the pointer has already left.
          if (productCard.matches(':hover')) playVideoEl(secondSlideVideo);
        }
        if (secondSlideVideo) {
          hideMedia(secondSlideImg);
          showMedia(secondSlideVideo);
        } else if (secondSlideImg && originalSecondary) {
          secondSlideImg.src = originalSecondary.src;
          secondSlideImg.srcset = originalSecondary.srcset;
          secondSlideImg.alt = originalSecondary.alt;
          showMedia(secondSlideImg);
        } else {
          hideMedia(secondSlideImg);
        }
        if (secondSlide) secondSlide.hidden = false;
        if (hoverDots) hoverDots.hidden = false;
      } else if (secondaryVideo && secondSlideVideo) {
        // Non-primary colourway with its OWN hover video: repoint the existing
        // <video> at that clip and play it, hiding the swap-in image.
        hideMedia(secondSlideImg);
        swapVideoSource(secondSlideVideo, secondaryVideo);
        videoSwapped = true;
        showMedia(secondSlideVideo);
        if (secondSlide) secondSlide.hidden = false;
        if (hoverDots) hoverDots.hidden = false;
      } else if (secondaryImage && secondSlideImg) {
        // Non-primary colourway with a secondary image: show it, hide the video.
        secondSlideImg.src = secondaryImage;
        secondSlideImg.srcset = secondarySrcset || '';
        secondSlideImg.alt = data.variationTitle || element.getAttribute('aria-label') || '';
        if (secondSlideVideo && videoSwapped) {
          restoreVideoSource(secondSlideVideo);
          videoSwapped = false;
        }
        hideMedia(secondSlideVideo);
        showMedia(secondSlideImg);
        if (secondSlide) secondSlide.hidden = false;
        if (hoverDots) hoverDots.hidden = false;
      } else {
        if (secondSlide) secondSlide.hidden = true;
        if (hoverDots) hoverDots.hidden = true;
      }
    }

    for (const thumb of container.querySelectorAll('[data-variation-hover-init]')) {
      thumb.classList.toggle('is-active', thumb === element);
    }
  }

  /** @param {HTMLElement} selected */
  function updateAriaCurrent(selected) {
    for (const thumb of container.querySelectorAll('[data-variation-hover-init]')) {
      if (thumb === selected) {
        thumb.setAttribute('aria-current', 'page');
      } else {
        thumb.removeAttribute('aria-current');
      }
    }
  }

  /** @param {MouseEvent} event */
  function handleClick(event) {
    if (!mediaQueryTouch.matches) return;

    const target = event.target instanceof Element ? event.target : null;
    const thumbnail = /** @type {HTMLElement | null} */ (target?.closest('[data-variation-hover-init]'));
    if (!thumbnail || thumbnail === selectedVariation) return;

    event.preventDefault();
    selectedVariation = thumbnail;
    applyVariation(thumbnail);
    updateAriaCurrent(thumbnail);
  }

  /** @param {MouseEvent | FocusEvent} event */
  function handleInteraction(event) {
    const target = /** @type {HTMLElement | null} */ (event.target);
    const thumbnail = /** @type {HTMLElement | null} */ (target?.closest('[data-variation-hover-init]'));
    if (thumbnail) applyVariation(thumbnail);
  }

  function handleLeave() {
    const fallback = selectedVariation || primaryVariation;
    if (fallback) applyVariation(fallback);
  }

  /** @param {FocusEvent} event */
  function handleFocusOut(event) {
    const relatedTarget = /** @type {HTMLElement | null} */ (event.relatedTarget);
    if (!productCard.contains(relatedTarget)) {
      const fallback = selectedVariation || primaryVariation;
      if (fallback) applyVariation(fallback);
    }
  }

  const listenerController = new AbortController();
  const { signal } = listenerController;

  container.addEventListener('mouseover', handleInteraction, { signal });
  container.addEventListener('focusin', handleInteraction, { signal });
  container.addEventListener('click', handleClick, { signal });
  productCard.addEventListener('mouseleave', handleLeave, { signal });
  productCard.addEventListener('focusout', handleFocusOut, { signal });

  const nonActiveThumbnails = /** @type {HTMLElement[]} */ (
    Array.from(container.querySelectorAll('[data-variation-hover-init]')).filter(
      (t) => t instanceof HTMLElement && !t.classList.contains('is-active'),
    )
  );
  const prefetchController = new AbortController();
  if (nonActiveThumbnails.length) {
    /** @type {Set<string>} */
    const warmed = new Set();
    // Warm the HTTP cache with real <img> requests (higher priority than
    // rel=prefetch, which the browser often defers) the moment the pointer
    // enters the card, so the first swatch hover swaps instantly instead of
    // waiting on a cold fetch. Both the main and the second (hover) image warm.
    /** @param {string | undefined} srcset */
    const warm = (srcset) => {
      if (!srcset || warmed.has(srcset)) return;
      warmed.add(srcset);
      const img = new Image();
      if (mainImageSizes) img.sizes = mainImageSizes;
      img.srcset = srcset;
    };
    productCard.addEventListener(
      'pointerenter',
      () => {
        for (const thumbnail of nonActiveThumbnails) {
          warm(thumbnail.dataset.variationHoverSrcset || thumbnail.dataset.variationImageSrcset);
          warm(thumbnail.dataset.variationHoverSecondarySrcset);
        }
      },
      { once: true, signal: prefetchController.signal },
    );
  }

  return () => {
    listenerController.abort();
    prefetchController.abort();
  };
}

/**
 * Lazily initialized card-variation-hover custom element.
 * @extends {Component}
 */
export class CardVariationHover extends Component {
  /** @type {HTMLElement | null} */
  #productCard = null;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {boolean} */
  #initialized = false;

  /** @type {(() => void) | null} */
  #unbind = null;

  connectedCallback() {
    super.connectedCallback();

    this.#productCard = this.closest('product-card') || this.closest('.resource-card');
    if (!this.#productCard) return;
    this.#observeVisibility();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#unbind?.();
    this.#unbind = null;
  }

  #observeVisibility() {
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !this.#initialized) {
            this.#initialize();
            this.#observer?.disconnect();
          }
        }
      },
      { rootMargin: '100px' },
    );

    this.#observer.observe(this);
  }

  #initialize() {
    if (!this.#productCard) return;
    this.#initialized = true;
    this.#unbind = bindCardVariationHover(this, this.#productCard);
  }
}

if (!customElements.get('card-variation-hover')) {
  customElements.define('card-variation-hover', CardVariationHover);
}

/*
 * Resource-card hover: second image/video on desktop hover + mobile swipe.
 * Desktop: CSS :hover on .resource-card shows slide 2 via opacity.
 * Mobile: native slideshow swipe + dots (desktop-disabled attribute).
 * JS only handles video play/pause.
 */

/** @param {HTMLVideoElement} video */
function playHoverVideo(video) {
  video.dataset.shouldPlay = 'true';

  if (video.readyState > 0) {
    try {
      video.currentTime = 0;
    } catch {}
    video.play()?.catch(() => {});
    return;
  }

  video.addEventListener(
    'loadeddata',
    () => {
      if (video.dataset.shouldPlay === 'true') video.play()?.catch(() => {});
    },
    { once: true },
  );
  video.load();
}

/** @param {HTMLVideoElement} video */
function pauseHoverVideo(video) {
  delete video.dataset.shouldPlay;
  video.pause();
  try {
    video.currentTime = 0;
  } catch {}
}

/** @param {Element} root */
function getHoverVideo(root) {
  return /** @type {HTMLVideoElement | null} */ (root.querySelector('video.resource-card__hover-video'));
}

document.addEventListener('pointerover', (event) => {
  if (!(event.target instanceof Element)) return;
  if (event.pointerType !== 'mouse') return;
  if (event.target.closest('card-variation-hover')) return;

  const card = event.target.closest('.resource-card');
  if (!(card instanceof HTMLElement)) return;

  const video = getHoverVideo(card);
  if (video) playHoverVideo(video);
});

document.addEventListener('pointerout', (event) => {
  if (!(event.target instanceof Element)) return;
  if (event.pointerType !== 'mouse') return;

  const card = event.target.closest('.resource-card');
  if (!(card instanceof HTMLElement)) return;
  if (event.relatedTarget instanceof Node && card.contains(event.relatedTarget)) return;

  const video = getHoverVideo(card);
  if (video) pauseHoverVideo(video);
});

document.addEventListener('slideshow:select', (event) => {
  if (!(event.target instanceof Element)) return;

  const slideshow = event.target.classList.contains('resource-card__hover-slideshow')
    ? event.target
    : event.target.closest('.resource-card__hover-slideshow');
  if (!slideshow) return;

  const video = getHoverVideo(slideshow);
  if (!video) return;

  const detail = /** @type {{ detail?: { slide?: Element } }} */ (event).detail;
  if (detail?.slide?.contains(video)) {
    playHoverVideo(video);
  } else {
    pauseHoverVideo(video);
  }
});

// iOS Safari ignores preload="auto" for off-screen videos. Force load()
// when resource cards with hover videos approach the viewport.
if ('IntersectionObserver' in window) {
  const warmObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const video = getHoverVideo(entry.target);
        if (video && video.readyState === 0) video.load();
        warmObserver.unobserve(entry.target);
      }
    },
    { rootMargin: '200px 0px' },
  );

  const observeCards = () => {
    for (const card of document.querySelectorAll('.resource-card:has(video.resource-card__hover-video)')) {
      warmObserver.observe(card);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeCards, { once: true });
  } else {
    observeCards();
  }
}
